import { Injectable } from "@angular/core";
import { ApiServiceCall } from "../global.apiServicecall";
import { ApiUrl } from "../../constants/apiUrl";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { OperationResult } from "../../models/operation-result";
import { MealModel } from "../../models/home/meal-model";
import { MealCategoryModel } from "../../models/home/meal-category-model";
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import { Address } from "../../models/address-model";

@Injectable()
export class PlanService {     
    
    constructor(
        private serviceCall: ApiServiceCall
        ) { }


    handleAssignResponse(): void {
        Notify.success('Expert assigned', {
            position: 'right-top',
            timeout: 3000
        });
    }

    handleError(error: any): void {
        Notify.failure(error.error.message + '- Operation unsuccessful', {
            position: 'right-top',
            timeout: 3000
        });
    }

    getExpert(id: number): Observable<OperationResult<any>> {
        let _url = ApiUrl.experts + '/' + id ;
        return this.serviceCall.GET(_url, true, null);
    }

    getExperts(): Observable<OperationResult<any>> {
        let _url = ApiUrl.publicExperts;
        return this.serviceCall.GET(_url, false, null);
    }

    assignExperts(id: number): void {
        let _url = ApiUrl.experts + '/' + id + '/assign';
        localStorage.setItem('expert', id.toString())
        this.serviceCall.Post(_url, true, null).subscribe({
            next: this.handleAssignResponse.bind(this),
            error: this.handleError.bind(this)
        });
    }

    unAssignExperts(id: number): Observable<ArrayBuffer> {
        let _url = ApiUrl.experts + '/' + id + '/unassign';
        return this.serviceCall.Delete(_url, true);
    }

    changeExpert(newId: number, oldId: number){
        localStorage.removeItem('expert');
        localStorage.setItem('expert', newId.toString())
        this.unAssignExperts(oldId).subscribe((response: any) => {
            if(response)
                this.assignExperts(newId);
        });        
    }

    changeDeliveryAddressForOrder(orderId: number, newAddress: Address): Observable<OperationResult<void>> {
        let _url = ApiUrl.orders + '/' + orderId + '/delivery-address';
        let data = { "zip": newAddress.zip, "address": newAddress.address, "city": newAddress.city};
        return this.serviceCall.Patch(_url, true, data);
    }

    saveAutoRenew(orderId: number, auto_renew: boolean): Observable<OperationResult<void>> {
        let _url = ApiUrl.orders + '/' + orderId + '/auto-renew';
        let data = { "order_id": orderId, "auto_renew": auto_renew};
        return this.serviceCall.Patch(_url, true, data);
    }

    cancelSubscription(orderId: number, reason: string, dateFrom: string): Observable<OperationResult<void>> {
        let _url = ApiUrl.orders + '/' + orderId + '/request-cancelation';
        let data = { "order_id": orderId, "reason": reason, "cancel_from": dateFrom};
        return this.serviceCall.Patch(_url, true, data);
    }

    pauseSubscription(orderId: number, pauseFrom: Date, pauseTo: Date): Observable<OperationResult<void>> {
        let _url = ApiUrl.orders + '/' + orderId + '/pause-subscription';
        let data = { "order_id": orderId, "pause_from": pauseFrom, "pause_to": pauseTo};
        return this.serviceCall.Patch(_url, true, data);
    } 

    

    getMeals(allergies: any = [], tags: any = [], mealCategories: any = [], limit: number = 0): Observable<MealModel[]> {
        let _url = ApiUrl.meals;
        let params: any = {};

        if (allergies.length > 0) params['exclude_allergies_ids[]'] = allergies;
        if (tags.length > 0) params['tag_ids[]'] = tags;
        if (mealCategories.length > 0) params['group_ids[]'] = mealCategories;
        if(limit > 0) params['limit'] = limit;
        
        return this.serviceCall.GET(_url, false, params).pipe(
            map((response: any) => {
                if (!response.body) throw new Error('Response body is null');
                return this.createMealsArray(response.body.data);
            })
        );
    }

    getMealsHome(allergies = null): Observable<OperationResult<any>> {
        let _url = ApiUrl.meals + "/home";
        return this.serviceCall.GET(_url, false, allergies);
    }

    getSelectedMeals(): Observable<MealModel[]> {
        let _url = ApiUrl.selectedMeals;
         return this.serviceCall.GET(_url, false).pipe(
            map((response: any) => {
                if (!response.body) throw new Error("Couldn't retrieve any meal!");
                return this.createMealsArray(response.body);
            })
        );
    }

    getAllergies(): Observable<OperationResult<any>> {
        let _url = ApiUrl.allergies;
        return this.serviceCall.GET(_url, false, null);
    }

    getMealCategories(): Observable<MealCategoryModel[]> {
        let _url = ApiUrl.mealCategories;
        return this.serviceCall.GET(_url, false, null).pipe(
            map((response: any) => {
                if (!response.body) throw new Error("Couldn't get any meal!");
                return this.createMealsCategoryArray(response.body);
            })
        );
    }

    getAddons(): Observable<OperationResult<any>> {
        let _url = ApiUrl.addons;
        return this.serviceCall.GET(_url, true, null);
    }

    activatePromoCode(data: any) {
        let _url = ApiUrl.discount + '/activate';
        return this.serviceCall.Post(_url, false, data);
    }

    getWeeklyDeliveriesCount(dayPerWeek: number, mealPerDay: number):  Observable<number> {
        let _url = ApiUrl.weeklyDeliveriesCount;
        return this.serviceCall.GET(_url, false, {dayPerWeek: dayPerWeek, mealPerDay: mealPerDay}).pipe(
            map((response: any) => {
                if (!response.body) throw new Error('Response body is null');
                return response.body.count;
            })
        );
    
    }


    createMealsCategoryArray(response: any) {
        let mealsCategoryModelArray: MealCategoryModel[] = [];
        response.forEach((item: any) => {
            var mealCategoryModel = new MealCategoryModel();
            mealCategoryModel.id = item.id;
            mealCategoryModel.name = item.name;

            //remove when icons will come from backend
            let mealIcon = '';
            switch(item.id){
                case 1: mealIcon = "chicken-thighs"; break;
                case 2: mealIcon = "steak"; break;
                case 3: mealIcon = "fish-food"; break;
                case 4: mealIcon = "leaf-01"; break;
                case 5: mealIcon = "organic-food"; break;
                case 6: mealIcon = "Gluten"; break;
            }
            mealCategoryModel.icon = "assets/images/" + mealIcon + ".svg";

            // temporary until we don't create backend function to disable it from there
            if (item.id !== 5 && item.id !== 6) {
                mealsCategoryModelArray.push(mealCategoryModel);
            }
        });

        return mealsCategoryModelArray;
    }


    createMealsArray(response: any) {
        let mealsModelArray: MealModel[] = [];
        response.forEach((item: any) => {
            var mealModel = new MealModel();
            mealModel.id = item.id;
            mealModel.name = item.name;
            mealModel.description = item.description;
            mealModel.type = item.type;
            mealModel.is_master_meal = item.is_master_meal;
            mealModel.photo_path = item.photo_path;
            mealModel.energyKcal = parseInt(item.energyKcal);
            mealModel.protein = parseInt(item.protein);
            mealModel.fat = parseInt(item.fat);
            mealModel.availableCarbohydrate = parseInt(item.availableCarbohydrate);
            mealModel.sugars = parseInt(item.sugars);
            mealModel.sodium = parseInt(item.sodium);
            mealModel.categoryId = item.categories.id;
            mealModel.categoryKey = item.categories.key_name;
            mealModel.categoryName = item.categories.name;
            mealModel.categories = item.categories;
            mealModel.labels = item.labels;
            mealModel.categories_meals = item.categories_meals;
            mealModel.salt = parseInt(item.salt);
            //mealModel.alergies = item.allergies;
            mealModel.alergies = '';
            let count = 0;
            item.allergies.map((x:any)=>x.name).forEach((allergy:any) => {
                if (count>0)
                mealModel.alergies +=  ", " + allergy;
                else mealModel.alergies += allergy;
                count++;
            });
            mealModel.quid = item.quid;
            mealModel.price = parseFloat(item.price);
            mealModel.week = 1;
            
            mealsModelArray.push(mealModel);
        });

        return mealsModelArray;
    }


    countTotalMeals(meals: MealModel[]): number {
        return meals.reduce((total, meal) => total + meal.count, 0);
    }


   
}